import { Button } from '@mui/material';
import type { GridRowsProp } from '@mui/x-data-grid';
import type { PriceTier } from '@weft/types';
import { MAX_TO } from '@weft/types';
import { withDefault } from '@weft/utils';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { addNewTier, adjustTiers } from '../../../../../../../utils/tiers-modifier';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { getEditTierTableColumns } from './edit-tiers-table-columns';
import { StyledUsageProductVariantTableEdit } from './styled-usage-product-variant-table-edit';
import type { UsageProductVariantTableRow } from './usage-product-variant-table-edit-types/usage-product-variant-table-row';

type UsageProductVariantTableEditProps = {
  tiers: Partial<PriceTier>[];
  productIndex: number;
  productVariantIndex: number;
  isVolumeProduct?: boolean;
  isRevShareProduct?: boolean;
  isTieredSubscriptionProduct?: boolean;
  isFlatRate?: boolean;
};

export const UsageProductVariantTableEdit: React.FC<UsageProductVariantTableEditProps> = ({
  tiers,
  productIndex,
  productVariantIndex,
  isVolumeProduct,
  isRevShareProduct,
  isTieredSubscriptionProduct,
  isFlatRate = false,
}) => {
  const { setValue, getValues } = useFormContext<PlanInputForm>();

  const addRow = useCallback(() => {
    const currentTiers = getValues(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`);
    const updatedTiers = addNewTier(currentTiers);
    setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`, updatedTiers);
  }, [getValues, productIndex, productVariantIndex, setValue]);

  const isFromCell = (field: string) => field === 'from';
  const isMaxToCell = (row: UsageProductVariantTableRow, value: number | undefined) => value === MAX_TO && row.index === tiers.length - 1;

  const handleRowUpdate = useCallback(({ id, index, ...priceTier }: UsageProductVariantTableRow, oldRow: UsageProductVariantTableRow) => {
    if (!isPriceTierValid(priceTier)) {
      return oldRow;
    }

    setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}`, priceTier);
    const currentTiers = getValues(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`);
    const adjustedTiers = adjustTiers(currentTiers);
    setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`, adjustedTiers);

    return {
      ...oldRow,
      ...priceTier,
    };
  }, [getValues, productIndex, productVariantIndex, setValue]);

  return (
    <div className='h-full w-full space-y-2 px-8 py-5'>
      <div className="mx-1">
        <StyledUsageProductVariantTableEdit
          columns={getEditTierTableColumns(isVolumeProduct ?? false, isRevShareProduct ?? false, isTieredSubscriptionProduct ?? false, isFlatRate)}
          rows={buildRows(tiers, productIndex, productVariantIndex)}
          hideFooter
          disableColumnMenu
          showCellVerticalBorder
          editMode="cell"
          autoHeight
          isCellEditable={({ value, row, field }) => !(isFromCell(field) || isMaxToCell(row, value as number | undefined))}
          processRowUpdate={handleRowUpdate}
          rowSelection={false}
        />
      </div>

      <div className="w-full text-end">
        <Button
          onClick={addRow}
          className='text-[#325890] text-[15px] font-light p-0'
          variant="text"
        >
          + Add Row
        </Button>
      </div>
    </div>
  );
};

function isPriceTierValid(tier: Partial<PriceTier>): boolean {
  const from = withDefault(tier?.from, 0);
  const to = withDefault(tier?.to, 0);

  return from < to;
}

function buildRows(tiers: Partial<PriceTier>[], productIndex: number, productVariantIndex: number): GridRowsProp<UsageProductVariantTableRow> {
  return tiers.map((tier, index) => ({
    ...tier,
    id: index.toString(),
    index,
    productIndex,
    productVariantIndex,
  }));
}